// RamsRuntimeQuestion — renders ONE live-answerable RAMS questionnaire
// question for the Phase 3 runtime (secure link + portal completion).
// Mirrors RamsPreview.jsx's conditional-logic semantics exactly, but every
// question type is actually functional here (real file/signature capture),
// not the builder's lightweight stub.
import { useRef, useState } from "react";
import { X, Plus, Loader2, UploadCloud, Sparkles } from "lucide-react";

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm disabled:bg-[#F6F4F0] disabled:text-[#9A968F]";

function SignaturePad({ value, onChange, disabled, testid }) {
    const ref = useRef(null);
    const drawing = useRef(false);
    function pos(e) {
        const c = ref.current, r = c.getBoundingClientRect();
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: cx - r.left, y: cy - r.top };
    }
    function start(e) { if (disabled) return; e.preventDefault(); drawing.current = true; const { x, y } = pos(e); ref.current.getContext("2d").beginPath(); ref.current.getContext("2d").moveTo(x, y); }
    function move(e) {
        if (!drawing.current || disabled) return; e.preventDefault();
        const { x, y } = pos(e); const ctx = ref.current.getContext("2d");
        ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#1F1F1F"; ctx.lineTo(x, y); ctx.stroke();
    }
    function end() { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); }
    function clear() { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); }
    return (
        <div>
            {value ? <img src={value} alt="signature" className="border rounded-lg h-24 bg-white" style={{ borderColor: "#E5E0DA" }} /> :
                <canvas ref={ref} width={420} height={100} data-testid={testid} onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                    onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                    className="w-full rounded-lg border bg-[#FCFBF9] touch-none" style={{ borderColor: "#E5E0DA" }} />}
            {!disabled && <button type="button" onClick={clear} className="text-[11px] text-[#9A968F] hover:text-[#1F1F1F] mt-1">Clear signature</button>}
        </div>
    );
}

function FileField({ question, files, onUpload, disabled }) {
    const [busy, setBusy] = useState(false);
    const mine = (files || []).filter((f) => f.field_key === question.field_key);
    return (
        <div>
            {mine.map((f) => <div key={f.id} className="text-[12px] text-[#2A5DB0] mb-1">{f.filename}</div>)}
            {!disabled && (
                <label className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border border-dashed border-[#E5E0DA] cursor-pointer hover:bg-[#F6F4F0]">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Upload {question.question_type}
                    <input type="file" className="hidden" accept={question.question_type === "photo" ? "image/*" : undefined}
                        onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setBusy(true); await onUpload(question.field_key, f); setBusy(false); e.target.value = ""; }} />
                </label>
            )}
        </div>
    );
}

function RepeatingGroup({ question, value, onChange, disabled }) {
    const rows = Array.isArray(value) ? value : [];
    const setRow = (i, key, v) => onChange(rows.map((r, j) => j === i ? { ...r, [key]: v } : r));
    return (
        <div className="space-y-2">
            {rows.map((row, i) => (
                <div key={i} className="p-2.5 rounded-lg border border-[#E5E0DA] bg-[#FBFAF8] space-y-1.5">
                    <div className="flex justify-between items-center">
                        <span className="text-[10.5px] font-bold text-[#9A968F] uppercase">Row {i + 1}</span>
                        {!disabled && <button onClick={() => onChange(rows.filter((_, j) => j !== i))}><X size={12} className="text-[#9A968F]" /></button>}
                    </div>
                    {(question.group_questions || []).map((gq) => (
                        <div key={gq.id}>
                            <label className="block text-[11px] text-[#6F6A64] mb-0.5">{gq.label}</label>
                            <input disabled={disabled} value={row[gq.field_key] || ""} onChange={(e) => setRow(i, gq.field_key, e.target.value)} className={inputCls} />
                        </div>
                    ))}
                </div>
            ))}
            {!disabled && (
                <button onClick={() => onChange([...rows, {}])}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--brand-accent,_#C75A1B)]"><Plus size={12} /> Add row</button>
            )}
        </div>
    );
}

export default function RamsRuntimeQuestion({ question: q, required, value, onChange, files, onUpload, disabled, testid, aiSuggested }) {
    const label = <label className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1F1F1F] mb-1">
        {q.label}{required && <span className="text-red-600 ml-0.5">*</span>}
        {aiSuggested && (
            <span data-testid={`${testid}-ai-badge`} title="Suggested by AI from your CPP/documents — please check this before submitting"
                className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#FBEFE5] text-[#C75A1B] border border-[#F0D8C0]">
                <Sparkles size={9} /> AI-suggested
            </span>
        )}
    </label>;

    if (q.question_type === "richtext_guidance") {
        return <div className="p-3 rounded-lg bg-[#FBEFE5] text-[12.5px] text-[#6F6A64] leading-relaxed">{q.help_text || q.label}</div>;
    }
    if (q.question_type === "yes_no") {
        return <div>{label}<div className="flex gap-2">
            {["Yes", "No"].map((opt) => (
                <button key={opt} type="button" disabled={disabled} data-testid={`${testid}-${opt.toLowerCase()}`} onClick={() => onChange(opt)}
                    className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold border disabled:opacity-60 ${value === opt ? "border-[var(--brand-accent,_#C75A1B)] bg-[#FBEFE5]" : "border-[#E5E0DA]"}`}>{opt}</button>
            ))}
        </div>{q.help_text && <p className="text-[11px] text-[#9A968F] mt-1">{q.help_text}</p>}</div>;
    }
    if (q.question_type === "select") {
        return <div>{label}<select data-testid={testid} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
            <option value="">Select…</option>{(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select></div>;
    }
    if (q.question_type === "multi_select" || q.question_type === "checkbox") {
        const arr = Array.isArray(value) ? value : [];
        return <div>{label}<div className="space-y-1">
            {(q.options || []).map((o) => (
                <label key={o} className="flex items-center gap-2 text-[12.5px] text-[#1F1F1F]">
                    <input type="checkbox" disabled={disabled} checked={arr.includes(o)}
                        onChange={() => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])} /> {o}
                </label>
            ))}
        </div></div>;
    }
    if (q.question_type === "textarea") {
        return <div>{label}<textarea data-testid={testid} rows={3} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls + " resize-none h-auto py-2"} /></div>;
    }
    if (q.question_type === "number") {
        return <div>{label}<input data-testid={testid} type="number" disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} /></div>;
    }
    if (q.question_type === "date" || q.question_type === "datetime") {
        return <div>{label}<input data-testid={testid} type={q.question_type === "date" ? "date" : "datetime-local"} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} /></div>;
    }
    if (q.question_type === "contact") {
        const c = value || {};
        return <div>{label}<div className="grid grid-cols-3 gap-1.5">
            {["name", "email", "phone"].map((k) => (
                <input key={k} disabled={disabled} placeholder={k} value={c[k] || ""} onChange={(e) => onChange({ ...c, [k]: e.target.value })} className={inputCls} />
            ))}
        </div></div>;
    }
    if (q.question_type === "address") {
        const a = value || {};
        return <div>{label}<div className="grid grid-cols-2 gap-1.5">
            {["line1", "line2", "city", "postcode"].map((k) => (
                <input key={k} disabled={disabled} placeholder={k} value={a[k] || ""} onChange={(e) => onChange({ ...a, [k]: e.target.value })} className={inputCls} />
            ))}
        </div></div>;
    }
    if (q.question_type === "repeating_group") {
        return <div>{label}<RepeatingGroup question={q} value={value} onChange={onChange} disabled={disabled} /></div>;
    }
    if (["file", "photo", "drawing"].includes(q.question_type)) {
        return <div>{label}<FileField question={q} files={files} onUpload={onUpload} disabled={disabled} /></div>;
    }
    if (q.question_type === "declaration_signature") {
        return <div>{label}<SignaturePad value={value} onChange={onChange} disabled={disabled} testid={testid} /></div>;
    }
    return <div>{label}<input data-testid={testid} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} /></div>;
}
